import {
  Banner,
  ModalV2,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  Text,
  Button,
  Flex,
  TextInputField,
} from '@electricjs/arc';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

type FormData = {
  confirmText: string;
};

const getSchema = (t: TFunction) =>
  yup.object().shape({
    confirmText: yup
      .string()
      .required()
      .equals(
        [t('assetDetailsButtonMenu.deleteModal.confirmPlaceholder')],
        t('assetDetailsButtonMenu.deleteModal.confirmError')
      ),
  });

type AssetDeleteModalProps = {
  onClose: () => void;
  onDelete: () => void;
};

const AssetDeleteModal = ({ onClose, onDelete }: AssetDeleteModalProps) => {
  const { t } = useTranslation('assets');
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormData>({
    resolver: yupResolver(getSchema(t)),
    mode: 'onChange',
    defaultValues: {
      confirmText: '',
    },
  });

  const handleDelete = () => {
    onDelete();
  };

  return (
    <ModalV2
      visible
      hide={onClose}
      ariaLabelledby="delete-asset-modal-header-text">
      <ModalHeader showCloseButton width="auto">
        <Text id="delete-asset-modal-header-text" variant="heading-2">
          {t('assetDetailsButtonMenu.deleteModal.title')}
        </Text>
      </ModalHeader>
      <form onSubmit={handleSubmit(handleDelete)}>
        <ModalBody>
          <Flex flexDirection="column" gap="1.6rem">
            <Text>{t('assetDetailsButtonMenu.deleteModal.message')}</Text>
            <Banner
              id="delete-asset-banner"
              intent="warning"
              width="100%"
              message={t('assetDetailsButtonMenu.deleteModal.warningMessage')}
            />

            <Text>
              {t('assetDetailsButtonMenu.deleteModal.confirmMessage')}
            </Text>
            <TextInputField
              id="confirm-delete-input"
              name="confirmText"
              control={control}
              label={t('assetDetailsButtonMenu.deleteModal.confirmLabel')}
              placeholder={t(
                'assetDetailsButtonMenu.deleteModal.confirmPlaceholder'
              )}
            />
          </Flex>
        </ModalBody>
        <ModalFooter>
          <ModalFooterActions>
            <Button id="cancel-delete-button" onClick={onClose} variant="text">
              {t('assetDetailsButtonMenu.deleteModal.cancel')}
            </Button>
            <Button
              id="confirm-delete-button"
              type="submit"
              variant="fill"
              intent="danger"
              disabled={!isValid}>
              {t('assetDetailsButtonMenu.deleteModal.delete')}
            </Button>
          </ModalFooterActions>
        </ModalFooter>
      </form>
    </ModalV2>
  );
};

export default AssetDeleteModal;
